import React from "react";
import TimePickerPicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

function TimePicker({
  value,
  onChange,
  format = "HH:mm",
  clockOnly = false,
  dropdownOnly = false,
  minuteStep = 1,
}) {
  const is12h = String(format).toLowerCase().includes("a");

  const parseTime = (rawValue) => {
    const [hRaw = "00", mRaw = "00"] = String(rawValue || "00:00").split(":");
    const hour = Number(hRaw);
    const minute = Number(mRaw);
    const safeHour = Number.isNaN(hour) ? 0 : Math.max(0, Math.min(23, hour));
    const safeMinute = Number.isNaN(minute)
      ? 0
      : Math.max(0, Math.min(59, minute));
    return { hour: safeHour, minute: safeMinute };
  };

  const to24hString = (hour, minute) =>
    `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

  const { hour, minute } = parseTime(value);

  if (dropdownOnly) {
    const safeMinuteStep = Math.min(30, Math.max(1, Number(minuteStep) || 1));
    const minuteOptions = Array.from(
      { length: Math.floor(59 / safeMinuteStep) + 1 },
      (_, i) => i * safeMinuteStep
    );

    if (is12h) {
      const period = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;

      const handleHour12Change = (e) => {
        const nextHour12 = Number(e.target.value);
        const normalized = nextHour12 % 12;
        const nextHour24 = period === "PM" ? normalized + 12 : normalized;
        onChange(to24hString(nextHour24, minute));
      };

      const handleMinuteChange = (e) => {
        const nextMinute = Number(e.target.value);
        onChange(to24hString(hour, nextMinute));
      };

      const handlePeriodChange = (e) => {
        const nextPeriod = e.target.value;
        let nextHour24 = hour;
        if (nextPeriod === "AM" && hour >= 12) nextHour24 = hour - 12;
        if (nextPeriod === "PM" && hour < 12) nextHour24 = hour + 12;
        onChange(to24hString(nextHour24, minute));
      };

      return (
        <div className="time-picker-dropdown d-flex align-items-center gap-1">
          <select
            className="form-control form-control-sm time-picker-dropdown-select"
            value={hour12}
            onChange={handleHour12Change}
            aria-label="Hour"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}
              </option>
            ))}
          </select>
          <span className="time-picker-dropdown-separator">:</span>
          <select
            className="form-control form-control-sm time-picker-dropdown-select"
            value={minute}
            onChange={handleMinuteChange}
            aria-label="Minute"
          >
            {minuteOptions.map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, "0")}
              </option>
            ))}
          </select>
          <div className="time-picker-period-toggle" role="group" aria-label="Period">
            <button
              type="button"
              className={`time-picker-period-btn${period === "AM" ? " is-active" : ""}`}
              onClick={() => handlePeriodChange({ target: { value: "AM" } })}
            >
              AM
            </button>
            <button
              type="button"
              className={`time-picker-period-btn${period === "PM" ? " is-active" : ""}`}
              onClick={() => handlePeriodChange({ target: { value: "PM" } })}
            >
              PM
            </button>
          </div>
        </div>
      );
    }

    const handleHourChange = (e) => {
      const nextHour = Number(e.target.value);
      onChange(to24hString(nextHour, minute));
    };

    const handleMinuteChange = (e) => {
      const nextMinute = Number(e.target.value);
      onChange(to24hString(hour, nextMinute));
    };

    return (
      <div className="time-picker-dropdown d-flex align-items-center gap-1">
        <select
          className="form-control form-control-sm time-picker-dropdown-select"
          value={hour}
          onChange={handleHourChange}
          aria-label="Hour"
        >
          {Array.from({ length: 24 }, (_, i) => i).map((h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, "0")}
            </option>
          ))}
        </select>
        <span className="time-picker-dropdown-separator">:</span>
        <select
          className="form-control form-control-sm time-picker-dropdown-select"
          value={minute}
          onChange={handleMinuteChange}
          aria-label="Minute"
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, "0")}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const preventTyping = (e) => {
    if (!clockOnly) return;
    e.preventDefault();
  };

  return (
    <div className={clockOnly ? "time-picker-clock-only" : ""}>
      <TimePickerPicker
        onChange={onChange}
        value={value}
        format={format}
        disableClock={!clockOnly}
        clearIcon={null}
        onKeyDown={preventTyping}
        onPaste={preventTyping}
      />
    </div>
  );
}
export default TimePicker;
